import React, { Component, createContext } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { connect } from 'react-redux'
import { mainLayoutSelector, sidebarSelector, modalOpenSelector } from 'selectors'
import styled, { css } from 'styled-components/macro'
import media from 'styles/mixins/media'
import Icon from 'components/misc/Icon'
import Avatar from 'components/misc/Avatar'
import { join } from 'util/path'
import FlashMessage from './FlashMessage'
import Messages from 'components/layout/Messages'
import { ModalProvider, ModalDisplayer, ModalConsumer } from 'components/layout/Modal'
import { body2, button } from 'styles/mixins/textstyles'
import { getMessages } from 'actions/profile'

export const context = createContext()
const { Provider } = context

export const Header = styled.header`
  background: ${props => props.theme.colors.primaryDarker};
  border-bottom: 1px solid ${props => props.theme.colors.primaryDarker};
  display: flex;
  align-items: center;
  justify-content: center;
`

export const HeaderContent = styled.div`
  height: 6.4rem;
  width: 100%;
  max-width: 102.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1.5rem;
  overflow: hidden;

  ${media.mobile`
    padding: 0;
  `}

  nav {
    display: flex;
  }
`

const LogoWrapper = styled.div`
  padding: 0 3.5rem;
  display: flex;
  align-items: center;

  ${media.mobile`
    padding: 0 2rem;
  `}
`

const RoadieLogoLink = styled(Link)`
  align-items: center;
  display: inline-flex;
`

const LogoText = styled(Icon).attrs({ symbol: 'logo-text' })`
  * {
    fill: white;
  }
  height: 4rem;

  ${media('desktop', 'tablet')`
    width: 16rem;
  `}
`

const MenuIcon = styled(Icon)`
  stroke: white;
  width: 3rem;
  margin-right: 1.5rem;
  cursor: pointer;

  ${media.desktop`
    display: none;
  `}
`

const FooterLink = styled.a`
  margin: 0 .2rem;
  font-weight: 700;

  &:not(:first-child)::before {
    content: "//";
    color: ${props => props.theme.colors.greyLight};
    margin-right: .4rem;
  }
`

const Footer = styled.footer`
  ${body2('black', 'medium')}

  text-align: center;
  margin: 9.6rem 0 3.2rem;
`

const Copyright = styled.div`
  margin-top: .4rem;
`

const linkStyle = css`
  ${button('white')}

  text-decoration: none;
  padding: 2.3rem 3.5rem;
  display: inline-block;

  &:hover {
    color: white;
  }

  ${media.mobile`
    padding: 2.3rem 2rem;
  `}
`

const activeClassName = 'nav-link-active'
const ActiveAppLink = styled(NavLink).attrs({
  activeClassName
})`
  ${linkStyle}

  &.${activeClassName} {
    background: rgba(0,0,0,.4);
  }
`

const HeaderLink = styled(Link)`
  ${linkStyle}
`

const AvatarLink = styled(Link)`
  ${button()}

  display: none;
  align-items: center;
  color: rgba(255,255,255,.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    color: white;
  }

  ${media.desktop`
    display: inline-flex;
  `}
`

const HeaderAvatar = styled(Avatar).attrs({ size: 32 })`
  margin-right: 1rem;
`

const PageWrapper = styled.div`
  margin: auto;
  padding: 5rem 5rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 102.5rem;

  ${media.mobile`
    padding: 0;
  `}
`

const SidebarWrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: calc(100vh - 28.2rem);

  > :not(:first-child) {
    flex-grow: 1;
    overflow: hidden;
  }
`

const Section = styled.section`
  z-index: 0;
  transition-delay: 350ms;
  display: flex;
  flex-direction: column;

  ${props => props.above && css`
    z-index: 1;
    transition-delay: 0ms;
  `}
`

const NavLinks = styled.nav`
  flex-shrink: 0;
`

const _NonModal = styled.div`
  ${props => props.modalOpen && css`
    filter: blur(3px);
  `}
`

const NonModal = connect(modalOpenSelector)(_NonModal)

const Sidebar = connect(sidebarSelector)(props => {
  if (props.sidebar) {
    return props.sidebar
  } else {
    return null
  }
})

const SidebarMenuIcon = connect(sidebarSelector)(props => {
  if (props.sidebar) {
    return <MenuIcon symbol="menu" onClick={props.onClick} />
  } else {
    return null
  }
})

class MainLayout extends Component {

  componentDidMount() {
    this.props.dispatch(getMessages())
  }

  renderAppLinks() {
    if (!this.props.appRootPath || this.props.appRootPath === '/') {
      return null
    }
    return (
      <NavLinks>
        <ActiveAppLink id="send-app-link" to="/send">Send</ActiveAppLink>
        <ActiveAppLink id="drive-app-link" to="/drive">Drive</ActiveAppLink>
      </NavLinks>
    )
  }

  renderUserLinks() {
    const { profile, appRootPath, isSigningUp } = this.props

    return (
      <NavLinks>
        <AvatarLink id="header-avatar" to={join(appRootPath, 'dashboard')}>
          <HeaderAvatar url={profile.avatar_url} />
          {profile.business_name ? profile.business_name : `${profile.first_name} ${(profile.last_name || '').substr(0, 1)}.`}
        </AvatarLink>
        <HeaderLink id="header-sign-out" to="/sign-out">{ isSigningUp ? 'Finish Later' : 'Sign Out' }</HeaderLink>
      </NavLinks>
    )
  }

  openSidebar = event => {
    event.preventDefault()
    this.props.dispatch({ type: 'SET_SIDEBAR_OPEN', open: true })
  }

  setSidebar = sidebar => {
    this.props.dispatch({ type: 'SET_SIDEBAR', sidebar })
  }

  render() {
    return (
      <ModalProvider>
        <NonModal>
        <Header>
          <HeaderContent>
            <LogoWrapper>
              <SidebarMenuIcon onClick={this.openSidebar} />
              <RoadieLogoLink id="logo" to={this.props.appRootPath || '/'}>
                <LogoText />
              </RoadieLogoLink>
            </LogoWrapper>
            {this.props.profile ? this.renderUserLinks() : this.renderAppLinks()}
          </HeaderContent>
        </Header>
        <ModalConsumer>
          {manager => <FlashMessage surpress={manager.getCurrentModal()} />}
        </ModalConsumer>
        
        <PageWrapper>
          <Provider value={{ setSidebar: this.setSidebar }}>
            <SidebarWrapper>
              <Section above={this.props.sidebarOpen}>
                <Sidebar />
              </Section>
              <Section>
                {this.props.children}
              </Section>
            </SidebarWrapper>
          </Provider>
        </PageWrapper>
        <Footer>
          <div>
            <FooterLink href="https://support.roadie.com" target="_blank" rel="noopener nofollower">Help</FooterLink>
            <FooterLink href="https://www.roadie.com/privacy" target="_blank" rel="noopener nofollower">Privacy</FooterLink>
            <FooterLink href="https://www.roadie.com/terms" target="_blank" rel="noopener nofollower">Terms</FooterLink>
          </div>
          <Copyright>©{new Date().getFullYear()} Roadie, Inc. All rights reserved</Copyright>
        </Footer>
        </NonModal>
        <ModalDisplayer />
        <Messages />
      </ModalProvider>
    )
  }
}

export default connect(mainLayoutSelector)(MainLayout)